import Cue from "./Cue";
import User from "./User";
import Detector from "./lib/Detector";
import Table from "./Table";
import TableModel from "./TableModel";
import RoomModel from "./RoomModel";
import World from "./World";
import GameState from "./GameState";
import Events from "./Events";
import Balls from "./Balls";
import Match from "./Match";
import {initialPositions} from "./shared/rules";
import {AUTO_QUALITY_PROBE, NO_RENDER} from "./testFlags";
import {bindEngine} from "./match/engine";
import {FpsCounter} from "./fpsCounter";
import {PlayerSettings} from "./settings";
import {QUALITY_PROFILES, QualityLevel, devicePixelRatioFor, effectiveQuality} from "./quality";
import {QualityController, preferLowOnPhone} from "./graphicsQuality";
import {detectCaps, isPhoneLike} from "./inputMode";
import {BallShadows} from "./ballShadows";
import {releaseOtherScales} from "./textures";


let stats:any;
let Game:any = {
    Config: {
        SinglePlayer: true,
        Anaglyph: false
    },
    Resources: {
        statsEnabled: true,
        placeholder: {},
        container: {},
        renderer: {},
        width: {},
        height: {}
    },
    Scenario: {
        scene: {},
        Lights: {},
        Objects: {},
        Bodyes: {},
        Camera: {},
        WhiteBall: { Killed: false }
    },
    // Turn/rules state lives in Match.ts (practice: local rules, online: server-authoritative).
    State: {
        Shot: function(){
            Match.onShotTaken();
        },
        MyTurn: function(){
            return Match.canShoot();
        }
    },
    InitResources: function (placeholderRef: HTMLElement){
        this.Resources.width = window.innerWidth-225;
        this.Resources.height = window.innerHeight-135;

        this.Resources.placeholder = placeholderRef;
        this.Resources.container = document.createElement('div');

        let placeholder:HTMLElement|null = placeholderRef;
        while (placeholder && placeholder.firstChild) {
            placeholder.removeChild(placeholder.firstChild);
        }

        placeholderRef.appendChild(this.Resources.container);

        // Graphics quality (quality.ts): antialiasing is fixed when the canvas is made, so a change follows on the next
        // load; the pixel ratio and the scene's detail follow at once (ApplyQuality).
        // Auto measures the frame rate only while drawing, and not under ?test=1 (testFlags.ts AUTO_QUALITY_PROBE)
        const canProbe = !NO_RENDER && AUTO_QUALITY_PROBE;
        // a phone or tablet on its first visit: Auto starts at Low (graphicsQuality.ts preferLowOnPhone); no frame rate
        // is needed for that, so also without drawing, but not under ?test=1 unless &autoquality=1
        preferLowOnPhone(PlayerSettings, isPhoneLike(detectCaps()), AUTO_QUALITY_PROBE);
        const profile = QUALITY_PROFILES[effectiveQuality(PlayerSettings.get())];
        this.Quality = new QualityController((level: QualityLevel) => Game.ApplyQuality(level), canProbe);

        if( Detector.webgl ){
            this.Resources.renderer = new THREE.WebGLRenderer({
                clearColor : 0x000000,
                clearAlpha : 1,
                antialias  : profile.antialias,	// to get smoother output (High)
                preserveDrawingBuffer : true,	// to allow screenshot
                devicePixelRatio : devicePixelRatioFor(profile, window.devicePixelRatio)
            });

            this.Resources.effect = new THREE.AnaglyphEffect( this.Resources.renderer );
            this.Resources.effect.setSize( this.Resources.width, this.Resources.height );
            // uncomment if webgl is required
        }else{
            Detector.addGetWebGLMessage();
            return true;
        }


        this.Resources.renderer.setSize(this.Resources.width, this.Resources.height);
        this.Resources.renderer.sortObjects = false;

        // No shadow map: the balls' shadows are soft blobs on the cloth (ballShadows.ts), cheaper than the second
        // render pass and the shadow lookups the spot light's shadow map cost, and without its blocky edges.
        this.Resources.renderer.shadowMapEnabled = false;
        this.Resources.renderer.setClearColor( 0x000000, 1 );

        if (this.Resources.statsEnabled) {
            stats = new Stats();
            stats.domElement.style.position = 'absolute';
            stats.domElement.style.top = '0px';
            stats.domElement.style.zIndex = 100;
            this.Resources.container.appendChild(stats.domElement);
            // hidden unless Settings > "Show FPS counter" is on (fpsCounter.ts)
            FpsCounter.bind(stats);
        }

        this.Resources.container.appendChild(this.Resources.renderer.domElement);
    },
    InitScene: function (){
        this.Scenario.scene = new THREE.Scene();
        this.Scenario.scene.fog = new THREE.Fog(0x000000, 1, 20000);

        const level: QualityLevel = this.Quality ? this.Quality.level : "high";
        const profile = QUALITY_PROFILES[level];
        Table.Init();
        // The table is built in code (TableModel.ts, from the same outlines as the physics), not loaded from a model.
        this.Scenario.TableRoot = TableModel.build(profile);
        this.Scenario.scene.add(this.Scenario.TableRoot);
        Cue.Init();

        // The room around it (floor, panelled walls, ceiling, the lamp, furniture), also built in code (RoomModel.ts).
        this.Scenario.RoomRoot = RoomModel.build(profile);
        this.Scenario.scene.add(this.Scenario.RoomRoot);

        // Every ball (Balls.ts: cue ball, 15 reds + 6 colours, the 15 numbered balls of Any ball) and their soft
        // shadows; the table starts with the layout of the practice frame Match starts with (the chosen ruleset).
        Balls.setSegments(profile.ballSegments);
        Balls.init(Match.frame.positions || initialPositions());
        this.Scenario.BallShadows = new BallShadows(Balls.list.length);
        this.Scenario.scene.add(this.Scenario.BallShadows.mesh);
        Match.init();

        return Promise.resolve();
    },
    InitLights: function(){
        var alight = new THREE.AmbientLight(0xffffff, 0.4);
        this.Scenario.scene.add(alight);

        var light = new THREE.SpotLight(0xffffff, 1.4);
        light.position.set(9, 27, 14);
        light.target.position.set( 0, 7, 0 );
        light.castShadow = false;   // see InitResources: no shadow map
        this.Scenario.scene.add(light);

    },
    InitCamera: function(){
        this.Camera = new THREE.PerspectiveCamera(60, this.Resources.width/ this.Resources.height, 1, 80000);
        this.Camera.position.z = 20;
        this.Camera.verticalAngle = Math.PI/4;
        this.Camera.horizontalAngle = Math.PI;
    },
    UpdateState: function(){
        if (Events.keysPressed.left == true){
            this.Camera.horizontalAngle -= GameState.horizontalSpeed;
        }
        if (Events.keysPressed.right == true){
            this.Camera.horizontalAngle += GameState.horizontalSpeed;
        }
        if (Events.keysPressed.up == true){
            if (this.Camera.verticalAngle < 3.0/2){
                this.Camera.verticalAngle += GameState.verticalSpeed;
            }
        }
        if (Events.keysPressed.down == true){
            if (this.Camera.verticalAngle > -3.0/2){
                this.Camera.verticalAngle -= GameState.verticalSpeed;
            }
        }

        try {
            if (Game.State.MyTurn()){
                Cue.RenderedBody.position.x = (Math.sin(this.Camera.horizontalAngle)*(Cue.Size + Cue.Distance)) + Game.Scenario.WhiteBall.PhysicalBody.GetPosition().x;
                Cue.RenderedBody.position.z = (Math.cos(this.Camera.horizontalAngle)*(Cue.Size + Cue.Distance)) + Game.Scenario.WhiteBall.PhysicalBody.GetPosition().y;
                Cue.RenderedBody.position.y =  Math.sin(Cue.verticalAngle)*(Cue.Size + Cue.Distance)+ 7.926842;
                Cue.RenderedBody.lookAt(Game.Scenario.WhiteBall.Rendered.position);
                Cue.RenderedBody.rotation.y += Math.PI;
            }
        } catch (e){
        }

        Match.update();

        if (!Match.physicsActive()){
            // Spectating the opponent's shot: positions come from the shooter (Match.playFrames).
            World.pause();
            return;
        }

        // Event-driven physics (World.ts, physics/solver.ts): advance by the real time this frame took (times the
        // test hooks' fast-forward), so the speed is the same at 30, 60 or 144 Hz. Contacts and pots arrive
        // through the listener at the exact moment they happen.
        World.advanceFrame(performance.now(), Match.stepsPerFrame, Game.PhysicsListener);

        let balls = Balls.onTable();
        // the meshes follow the bodies and roll as they go (visual only: ballRoll.ts)
        for (let i=0, j=balls.length; i<j; ++i){
            Balls.follow(balls[i]);
        }

        if (Match.isShooting()){
            if (World.sim.allStopped()){
                Match.onAllResting();
            } else {
                Match.afterStep();
            }
        }
    },

    PhysicsListener: {
        onBallContact: function(a: string, b: string){
            Match.onContact(a, b); // first ball hit (rules)
            if (Cue.HasShot) Events.BallHit();
        },
        onPotted: function(id: string){
            const ball = Balls.byId[id];
            if (!ball) return;
            Balls.kill(ball);
            Match.onPotted(id);
        },
        onEventCap: function(events: number){
            console.warn(`physics: ${events} events in one shot, stopping the balls where they are`);
        }
    },

    UpdateCamera: function(){

        this.Camera.position.y = Math.sin(this.Camera.verticalAngle)*GameState.cameraDistance + 7.926842;

        this.Camera.position.x = Math.cos(this.Camera.verticalAngle)*Math.sin(this.Camera.horizontalAngle)*GameState.cameraDistance + Game.Scenario.WhiteBall.PhysicalBody.GetPosition().x;
        this.Camera.position.z = Math.cos(this.Camera.verticalAngle)*Math.cos(this.Camera.horizontalAngle)*GameState.cameraDistance + Game.Scenario.WhiteBall.PhysicalBody.GetPosition().y;

        this.Camera.lookAt(Game.Scenario.WhiteBall.Rendered.position);
    },

    Render: function(){
        let containerRect = this.Resources.placeholder.getBoundingClientRect()
        this.Resources.width = containerRect.width;
        this.Resources.height = containerRect.height;

        this.SyncCanvasSize();
        this.SyncViewport();

        // the lamp over the table fades out as the camera rises towards it (RoomModel, roomLayout.lampOpacity)
        RoomModel.update(this.Camera);
        // the soft shadows follow the balls on the table (potted balls have none)
        if (this.Scenario.BallShadows) this.Scenario.BallShadows.update(Balls.onTable().map(b => b.Rendered.position));

        // ?test=1&norender=1 (e2e only, see testFlags.ts): skip the WebGL draw (and with it the shadow map pass).
        // Everything else in the frame (state, physics, camera, cue, HUD) has already run as usual.
        if (NO_RENDER){
            // nothing drawn
        } else if (Game.Config.Anaglyph){
            this.Resources.effect.render(this.Scenario.scene, this.Camera);
        } else {
            this.Resources.renderer.render(this.Scenario.scene, this.Camera);
        }

        FpsCounter.update();
        // Auto graphics quality: measures the first seconds' frame rate (graphicsQuality.ts)
        if (this.Quality) this.Quality.frame(performance.now());
    },

    /**
     * Sizes the canvas to its box at the quality's pixel ratio, only when that changed: setting a canvas's width or
     * height, even to the same value, reallocates its drawing buffer, which this did on every frame before.
     */
    SyncCanvasSize: function(){
        const r = this.Resources.renderer;
        const w = this.Resources.width, h = this.Resources.height;
        const canvas = r.domElement;
        if (canvas.width === Math.floor(w * r.devicePixelRatio) && canvas.height === Math.floor(h * r.devicePixelRatio)
            && this.Resources.cssWidth === w && this.Resources.cssHeight === h) return;
        r.setSize(w, h);
        // three.js r58 only sets the CSS size when the pixel ratio isn't 1; set it always (Low caps the ratio at 1)
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        this.Resources.cssWidth = w;
        this.Resources.cssHeight = h;
    },

    /** Graphics quality changed (Settings, or Auto measured): rebuild the scene's detail for it, without a reload. */
    ApplyQuality: function(level: QualityLevel){
        const profile = QUALITY_PROFILES[level];
        const r = this.Resources.renderer;
        if (r && r.domElement) {
            r.devicePixelRatio = devicePixelRatioFor(profile, window.devicePixelRatio);
            this.Resources.cssWidth = -1;   // resize on the next frame
        }
        const scene = this.Scenario.scene;
        if (!scene || !scene.add || !this.Scenario.TableRoot) return;
        const replace = (old: any, next: any) => {
            scene.remove(old);
            old.traverse((o: any) => {
                if (o.geometry && o.geometry.dispose) o.geometry.dispose();
                const mats = o.material ? (o.material.materials || [o.material]) : [];
                for (const m of mats) if (m && m.dispose) m.dispose();
            });
            scene.add(next);
            return next;
        };
        this.Scenario.TableRoot = replace(this.Scenario.TableRoot, TableModel.build(profile));
        this.Scenario.RoomRoot = replace(this.Scenario.RoomRoot, RoomModel.build(profile));
        Balls.setSegments(profile.ballSegments);
        releaseOtherScales(profile.textureScale);
    },

    // Keeps the camera aspect and the anaglyph render targets in sync with the canvas size
    // (window resize, fullscreen enter/exit, anaglyph toggle). Rendering only: no gameplay changes.
    SyncViewport: function(){
        let width = Math.floor(this.Resources.width);
        let height = Math.floor(this.Resources.height);
        if (!(width > 0 && height > 0) || !this.Camera || !this.Camera.updateProjectionMatrix) return;

        if (this.Resources.viewportWidth !== width || this.Resources.viewportHeight !== height){
            this.Resources.viewportWidth = width;
            this.Resources.viewportHeight = height;
            this.Camera.aspect = width / height;
            this.Camera.updateProjectionMatrix();
        }

        if (Game.Config.Anaglyph && this.Resources.effect &&
            (this.Resources.effectWidth !== width || this.Resources.effectHeight !== height)){
            this.Resources.effectWidth = width;
            this.Resources.effectHeight = height;
            this.Resources.effect.setSize(width, height);
        }
    }
};

// Match uses the camera and the cue through these (match/engine.ts); getters, so the cycle with Cue.ts is harmless.
bindEngine({get game() { return Game; }, get cue() { return Cue; }});

export default Game;